import {
  Alert,
  Badge,
  Button,
  Center,
  Checkbox,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { LoaderFunctionArgs, MetaFunction, json } from "@remix-run/node";
import { Link, useLoaderData } from "@remix-run/react";
import {
  IconBook2,
  IconExclamationCircle,
  IconSearch,
  IconSchool,
  IconStethoscope,
  IconVideo,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";

import { MasterclassCard } from "~/components/masterclass-card";
import { PAGE_TITLE } from "~/constants";
import i18n from "~/localization/i18n";
import i18nextServer from "~/localization/i18n.server";
import { getMasterclasses } from "~/services/get-masterclasses";
import utilityClasses from "~/styles/utilities.module.scss";
import { Masterclass } from "~/types/masterclass";
import { resolveCurrencyContext } from "~/utils/currency.server";
import { getCentralizedMeta } from "~/utils/meta-helper";
import { normalizeString } from "~/utils/normalize-string";

import classes from "./health-library.module.scss";

interface ResourceItem {
  id: string;
  title: string;
  description: string;
  type: "ebook" | "video";
  price?: number;
}

interface CuratedVideo {
  id: string;
  title: string;
  doctorName: string;
  specialty: string;
  doctorSlug: string;
  platform: "instagram";
  embedUrl: string;
}

const ebooks: ResourceItem[] = [];
const videos: CuratedVideo[] = [
  {
    id: "lynda-noufack-instagram-reel-1",
    title: "Vidéo santé avec Dr Lynda Noufack",
    doctorName: "Dr Lynda Noufack",
    specialty: "Spécialiste La Ruche",
    doctorSlug: "lynda-noufack",
    platform: "instagram",
    embedUrl: "https://www.instagram.com/reel/DYfd1kOu4ox/embed",
  },
  {
    id: "lynda-noufack-instagram-reel-2",
    title: "Conseils santé avec Dr Lynda Noufack",
    doctorName: "Dr Lynda Noufack",
    specialty: "Spécialiste La Ruche",
    doctorSlug: "lynda-noufack",
    platform: "instagram",
    embedUrl: "https://www.instagram.com/reel/DYup2-ioIV8/embed",
  },
  {
    id: "ngaoundere-instagram-reel-1",
    title: "Vidéo santé avec Dr Ngaoundere",
    doctorName: "Dr Ngaoundere",
    specialty: "Spécialiste La Ruche",
    doctorSlug: "ngaoundere",
    platform: "instagram",
    embedUrl: "https://www.instagram.com/reel/DVeeo86AFsq/embed",
  },
  {
    id: "ngaoundere-instagram-reel-2",
    title: "Conseils santé avec Dr Ngaoundere",
    doctorName: "Dr Ngaoundere",
    specialty: "Spécialiste La Ruche",
    doctorSlug: "ngaoundere",
    platform: "instagram",
    embedUrl: "https://www.instagram.com/reel/DRfN2NSjYXO/embed",
  },
];

export const meta: MetaFunction<typeof loader> = ({ data, location }) => {
  if (!data) return [];

  const meta = getCentralizedMeta({
    title: data.title,
    description: data.description,
    image: `${data.origin}/og/health-library-og-${data.locale === "fr" ? "fr" : "en"}.png`,
    url: data.origin + location.pathname,
    locale: data.locale,
    keywords:
      data.locale === "fr"
        ? "bibliothèque santé, masterclasses santé, ebooks santé, vidéos santé, éducation santé Afrique"
        : "health library, health masterclasses, health ebooks, health videos, health education Africa",
    alternateLanguages: {
      en: `${data.origin}/en/health-library`,
      fr: `${data.origin}/fr/health-library`,
    },
  });

  if (!data.masterclasses.length) {
    meta.push({ name: "robots", content: "noindex, nofollow" });
  }

  meta.push(
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    {
      name: "twitter:image:alt",
      content:
        data.locale === "fr"
          ? "Centre d'apprentissage santé de La Ruche"
          : "La Ruche Health Learning Center",
    }
  );

  return meta;
};

export default function HealthLibraryPage() {
  const loaderData = useLoaderData<typeof loader>();
  const locale = loaderData.locale;
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 250);
  const [selectedEbooks, setSelectedEbooks] = useState<string[]>([]);

  const filteredMasterclasses = useMemo(() => {
    const query = normalizeString(debouncedSearch);
    if (!query) return loaderData.masterclasses;

    return loaderData.masterclasses.filter(masterclass => {
      const searchable = [
        masterclass.name,
        masterclass.description,
        masterclass.providerDetails?.fullName,
        masterclass.providerDetails?.specialization,
      ]
        .filter(Boolean)
        .join(" ");

      return normalizeString(searchable).includes(query);
    });
  }, [debouncedSearch, loaderData.masterclasses]);

  const filteredEbooks = useMemo(
    () => filterResources(ebooks, debouncedSearch),
    [debouncedSearch]
  );
  const filteredVideos = useMemo(() => {
    const query = normalizeString(debouncedSearch);
    if (!query) return videos;

    return videos.filter(video =>
      normalizeString(
        `${video.title} ${video.doctorName} ${video.specialty}`
      ).includes(query)
    );
  }, [debouncedSearch]);

  const handleShare = async (masterclass: Masterclass) => {
    if (!navigator.share) return;

    const url = `${window.location.origin}/${locale}/masterclass/${masterclass.slug}`;

    try {
      await navigator.share({
        title: masterclass.name,
        text:
          locale === "fr"
            ? "Découvrez cette masterclass sur La Ruche Health."
            : "Explore this masterclass on La Ruche Health.",
        url,
      });
    } catch (error) {
      import.meta.env.DEV && console.error("[DEV] Share failed:", error);
    }
  };

  return (
    <main className={classes.section}>
      <div className={utilityClasses.container}>
        <Stack align="center" gap="sm" className={classes.hero}>
          <Title order={1} ta="center" fw={600} className={classes.heading}>
            {locale === "fr"
              ? "Centre d'apprentissage santé"
              : "Health Learning Center"}
          </Title>
          <Text maw={720} ta="center" c="dimmed" fz={{ base: 14, sm: 16 }}>
            {locale === "fr"
              ? "Explorez les masterclasses, ebooks et vidéos santé des spécialistes de La Ruche pour mieux comprendre votre santé et préparer vos consultations."
              : "Explore doctor-led masterclasses, ebooks, and health videos from La Ruche specialists to better understand your health and prepare for consultations."}
          </Text>
        </Stack>

        {loaderData.error ? (
          <Alert
            mt="lg"
            variant="light"
            color="red"
            icon={<IconExclamationCircle />}>
            {locale === "fr"
              ? "Impossible de charger les masterclasses pour le moment."
              : "Unable to load masterclasses right now."}
          </Alert>
        ) : null}

        <Tabs
          defaultValue="masterclasses"
          mt="2rem"
          keepMounted={false}
          classNames={{ root: classes.tabs }}>
          <Stack align="center" gap="md" className={classes.toolbar}>
            <Tabs.List className={classes["tabs-list"]}>
              <Tabs.Tab value="masterclasses">
                <span className={classes["tab-label"]}>
                  <IconSchool size={16} stroke={1.7} />
                  {locale === "fr" ? "Masterclasses" : "Masterclasses"}
                </span>
              </Tabs.Tab>
              <span className={classes.divider} aria-hidden="true">
                |
              </span>
              <Tabs.Tab value="ebooks">
                <span className={classes["tab-label"]}>
                  <IconBook2 size={16} stroke={1.7} />
                  {locale === "fr" ? "Ebooks" : "Ebooks"}
                </span>
              </Tabs.Tab>
              <span className={classes.divider} aria-hidden="true">
                |
              </span>
              <Tabs.Tab value="videos">
                <span className={classes["tab-label"]}>
                  <IconVideo size={16} stroke={1.7} />
                  {locale === "fr" ? "Vidéos" : "Videos"}
                </span>
              </Tabs.Tab>
            </Tabs.List>

            <TextInput
              className={classes.search}
              type="search"
              placeholder={
                locale === "fr"
                  ? "Rechercher"
                  : "Search"
              }
              leftSection={<IconSearch size={18} />}
              value={search}
              onChange={event => setSearch(event.currentTarget.value)}
            />

            <Button
              component={Link}
              to={`/${locale}`}
              variant="subtle"
              size="compact-sm"
              radius="xl"
              leftSection={<IconStethoscope size={16} stroke={1.7} />}>
              {locale === "fr"
                ? "Voir l'annuaire des spécialistes"
                : "Browse specialist directory"}
            </Button>
          </Stack>

          <Tabs.Panel value="masterclasses" pt="xl">
            <Group justify="space-between" mb="md">
              <Stack gap={2}>
                <Title order={2} fz={{ base: 20, sm: 24 }} fw={600}>
                  {locale === "fr"
                    ? "Masterclasses de médecins"
                    : "Doctor-led masterclasses"}
                </Title>
                <Text c="dimmed" fz={14}>
                  {locale === "fr"
                    ? "Sessions animées par des praticiens et experts santé."
                    : "Sessions hosted by practitioners and health experts."}
                </Text>
              </Stack>
              <Badge variant="light" color="gray">
                {filteredMasterclasses.length}
              </Badge>
            </Group>

            {filteredMasterclasses.length ? (
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
                {filteredMasterclasses.map(masterclass => {
                  const providerSlug = masterclass.providerDetails?.slug;

                  return (
                    <MasterclassCard
                      key={masterclass.id}
                      masterclass={masterclass}
                      locale={locale}
                      currency={loaderData.currency}
                      enrollLabel={locale === "fr" ? "S'inscrire" : "Enroll"}
                      shareLabel={locale === "fr" ? "Partager" : "Share"}
                      href={`/${locale}/masterclass/${masterclass.slug}`}
                      specialistHref={
                        providerSlug ? `/${locale}/doctor/${providerSlug}` : undefined
                      }
                      specialistLabel={
                        locale === "fr" ? "Voir le spécialiste" : "View specialist"
                      }
                      onShare={handleShare}
                    />
                  );
                })}
              </SimpleGrid>
            ) : (
              <EmptyState
                title={
                  locale === "fr"
                    ? "Aucune masterclass disponible"
                    : "No masterclasses available"
                }
                description={
                  search
                    ? locale === "fr"
                      ? "Essayez une autre recherche."
                      : "Try a different search."
                    : locale === "fr"
                      ? "Les prochaines masterclasses apparaîtront ici dès qu'elles seront disponibles."
                      : "Upcoming masterclasses will appear here as soon as they are available."
                }
              />
            )}
          </Tabs.Panel>

          <Tabs.Panel value="ebooks" pt="xl">
            <Group justify="space-between" mb="md">
              <Stack gap={2}>
                <Title order={2} fz={{ base: 20, sm: 24 }} fw={600}>
                  {locale === "fr" ? "Ebooks santé" : "Health ebooks"}
                </Title>
                <Text c="dimmed" fz={14}>
                  {locale === "fr"
                    ? "Guides téléchargeables et ressources pratiques."
                    : "Downloadable guides and practical resources."}
                </Text>
              </Stack>
              <Badge variant="light" color="gray">
                {filteredEbooks.length}
              </Badge>
            </Group>

            <div className={classes["resource-layout"]}>
              <Stack gap="md">
                {filteredEbooks.length ? (
                  filteredEbooks.map(ebook => (
                    <ResourceSelectableCard
                      key={ebook.id}
                      item={ebook}
                      checked={selectedEbooks.includes(ebook.id)}
                      onChange={checked =>
                        setSelectedEbooks(current =>
                          checked
                            ? [...current, ebook.id]
                            : current.filter(id => id !== ebook.id)
                        )
                      }
                    />
                  ))
                ) : (
                  <EmptyState
                    icon={<IconBook2 size={24} />}
                    title={
                      locale === "fr"
                        ? "Les ebooks arrivent bientôt"
                        : "Ebooks are coming soon"
                    }
                    description={
                      locale === "fr"
                        ? "Des guides pratiques seront bientôt disponibles pour vous accompagner avant et après vos consultations."
                        : "Practical guides will be available soon to support you before and after consultations."
                    }
                  />
                )}
              </Stack>

              <Paper withBorder p="md" className={classes.summary}>
                <Title order={2} fz={18} fw={600}>
                  {locale === "fr" ? "Ebooks sélectionnés" : "Selected ebooks"}{" "}
                  ({selectedEbooks.length})
                </Title>
                <Text c="dimmed" fz={14} mt="sm">
                  {locale === "fr"
                    ? "Les ebooks choisis apparaîtront ici avant le paiement ou le téléchargement."
                    : "Chosen ebooks will appear here before payment or download."}
                </Text>
                <Button mt="lg" fullWidth disabled={!selectedEbooks.length}>
                  {locale === "fr" ? "Continuer" : "Continue"}
                </Button>
              </Paper>
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="videos" pt="xl">
            <Group justify="space-between" mb="md">
              <Stack gap={2}>
                <Title order={2} fz={{ base: 20, sm: 24 }} fw={600}>
                  {locale === "fr" ? "Vidéos santé" : "Health videos"}
                </Title>
                <Text c="dimmed" fz={14}>
                  {locale === "fr"
                    ? "Explications courtes, interviews et contenus éducatifs."
                    : "Short explainers, interviews, and educational content."}
                </Text>
              </Stack>
              <Badge variant="light" color="gray">
                {filteredVideos.length}
              </Badge>
            </Group>

            {filteredVideos.length ? (
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
                {filteredVideos.map(video => (
                  <CuratedVideoCard
                    key={video.id}
                    video={video}
                    locale={locale}
                  />
                ))}
              </SimpleGrid>
            ) : (
              <EmptyState
                icon={<IconVideo size={24} />}
                title={
                  locale === "fr"
                    ? "Les vidéos arrivent bientôt"
                    : "Videos are coming soon"
                }
                description={
                  locale === "fr"
                    ? "Des vidéos éducatives seront bientôt disponibles pour expliquer les sujets de santé de façon simple et pratique."
                    : "Educational videos will be available soon to explain health topics in a simple, practical way."
                }
              />
            )}
          </Tabs.Panel>
        </Tabs>
      </div>
    </main>
  );
}

const filterResources = (items: ResourceItem[], search: string) => {
  const query = normalizeString(search);
  if (!query) return items;

  return items.filter(item =>
    normalizeString(`${item.title} ${item.description}`).includes(query)
  );
};

const EmptyState = ({
  icon,
  title,
  description,
}: {
  icon?: React.ReactNode;
  title: string;
  description: string;
}) => (
  <Center className={classes.empty}>
    <Stack align="center" gap="xs">
      {icon ? <div className={classes["empty-icon"]}>{icon}</div> : null}
      <Title order={2} fz={20} ta="center">
        {title}
      </Title>
      <Text c="dimmed" ta="center" maw={560}>
        {description}
      </Text>
    </Stack>
  </Center>
);

const ResourceSelectableCard = ({
  item,
  checked,
  onChange,
}: {
  item: ResourceItem;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <Paper withBorder p="md" radius="md">
    <Checkbox
      checked={checked}
      onChange={event => onChange(event.currentTarget.checked)}
      label={
        <Stack gap={4}>
          <Text fw={600}>{item.title}</Text>
          <Text c="dimmed" fz={13}>
            {item.description}
          </Text>
        </Stack>
      }
    />
  </Paper>
);

const CuratedVideoCard = ({
  video,
  locale,
}: {
  video: CuratedVideo;
  locale: string;
}) => (
  <Paper withBorder radius="md" className={classes["video-card"]}>
    <iframe
      className={classes["video-embed"]}
      src={video.embedUrl}
      title={video.title}
      loading="lazy"
      allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
      allowFullScreen
    />
    <Stack gap="xs" p="md">
      <Badge variant="light" color="green" w="fit-content">
        {locale === "fr" ? "Gratuit" : "Free"}
      </Badge>
      <Stack gap={2}>
        <Text fw={600}>{video.title}</Text>
        <Text c="dimmed" fz={13}>
          {video.doctorName} · {video.specialty}
        </Text>
      </Stack>
      <Button
        component={Link}
        to={`/${locale}/doctor/${video.doctorSlug}`}
        radius="xl"
        size="compact-sm"
        mt="xs"
        w="fit-content">
        {locale === "fr" ? "Consulter ce médecin" : "Consult with doctor"}
      </Button>
    </Stack>
  </Paper>
);

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  const lang = params.lang ?? "";
  const locale = i18n.supportedLngs.includes(lang.toLocaleLowerCase())
    ? lang
    : await i18nextServer.getLocale(request);
  const origin = new URL(request.url).origin;
  const currencyContext = (await resolveCurrencyContext(request, true)) ?? {
    currency: "XOF",
    fxRate: 1,
    country: "ci",
  };
  const { currency, fxRate } = currencyContext;

  const masterclassesRes = await getMasterclasses();
  const masterclasses = masterclassesRes.data.map(masterclass => ({
    ...masterclass,
    originalPrice: masterclass.price,
    price: masterclass.price * fxRate,
  }));

  return json({
    locale,
    origin,
    currency,
    masterclasses,
    error: masterclassesRes.ok ? null : masterclassesRes.error,
    title:
      locale === "fr"
        ? `Centre d'apprentissage santé • ${PAGE_TITLE}`
        : `Health Learning Center • ${PAGE_TITLE}`,
    description:
      locale === "fr"
        ? "Explorez les masterclasses, ebooks et vidéos santé des spécialistes de La Ruche pour mieux comprendre votre santé et préparer vos consultations."
        : "Explore doctor-led masterclasses, ebooks, and health videos from La Ruche specialists to better understand your health and prepare for consultations.",
  });
};
